import { Component, type ErrorInfo, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';

interface Props {
  children: ReactNode;
  /** Optional reset key — changing it clears the error (e.g. route path). */
  resetKey?: string;
}

interface State {
  error: Error | null;
}

/** Catches render-time errors anywhere below it and shows a recoverable screen. */
export class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidUpdate(prev: Props) {
    if (prev.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Wire to a real logger/monitoring service here.
    console.error('Unhandled UI error:', error, info.componentStack);
  }

  handleReload = () => {
    window.location.assign('/');
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <Container maxWidth="sm" sx={{ py: 10 }}>
        <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box
            sx={{ color: 'error.main', fontSize: 56, display: 'flex', justifyContent: 'center' }}
          >
            <ReportProblemOutlinedIcon fontSize="inherit" />
          </Box>
          <Typography variant="h2" component="h1">
            Something went wrong
          </Typography>
          <Typography color="text.secondary">
            An unexpected error occurred. You can return to the dashboard and try again.
          </Typography>
          {import.meta.env.DEV ? (
            <Typography
              component="pre"
              variant="caption"
              sx={{
                mt: 1,
                p: 1.5,
                textAlign: 'left',
                bgcolor: 'action.hover',
                borderRadius: 1,
                overflowX: 'auto',
              }}
            >
              {this.state.error.message}
            </Typography>
          ) : null}
          <Button
            variant="contained"
            onClick={this.handleReload}
            sx={{ mt: 1, alignSelf: 'center' }}
          >
            Back to dashboard
          </Button>
        </Box>
      </Container>
    );
  }
}
